import { Link } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import { heroPhotos, heroVideo } from '../content/media'
import { missions } from '../content/missions'
import { displayFonts, monoFont, sansFonts } from '../content/fonts'
import { sources } from '../content/system'
import Cased from '@/components/ui/Cased'

const typefaces = [...displayFonts, ...sansFonts.filter((font) => !displayFonts.some((d) => d.id === font.id)), monoFont]

export default function CreditsPage() {
  const media = missions
    .map((mission) => ({
      mission,
      items: [
        ...(mission.id === 'america' ? [...heroPhotos, ...(heroVideo ? [heroVideo] : [])] : []),
        ...(mission.media?.photos ?? []),
        ...(mission.media?.video ? [mission.media.video] : []),
      ],
    }))
    .filter((entry) => entry.items.length > 0)
  const marks = missions.filter((mission) => mission.seal || mission.logo)

  return (
    <DocLayout section={{ label: 'About', to: '/about' }} crumb="Credits"
      eyebrow="Credits"
      title="Built on the work of others."
      intro="Mission Design System stands on federal design work that came before it, and on photographs, seals and typefaces made by others."
    >
      <section className="doc-section">
        <h2><Cased>National Design Studio</Cased></h2>
        <p>
          MDS takes its visual direction from the{' '}
          <a href={sources.nds.url}>National Design Studio</a> and the America by Design initiative: a display serif, a
          precise sans, red, white and blue, and sites that put the mission first.
        </p>
      </section>
      <section className="doc-section">
        <h2><Cased>U.S. Web Design System</Cased></h2>
        <p>
          The <a href={sources.uswds.url}>U.S. Web Design System</a>, launched in 2015 by 18F and the U.S. Digital
          Service, gave federal websites a shared foundation for a decade. MDS follows its banner, identifier and
          interaction conventions, and maps every component to its USWDS equivalent so teams can migrate at their
          own pace.
        </p>
      </section>
      <section className="doc-section">
        <h2><Cased>Photographs and video</Cased></h2>
        <p>Photographs and video loops by federal agencies and their photographers, credited as published.</p>
        <dl className="doc-credits">
          {media.map(({ mission, items }) => (
            <div key={mission.id}>
              <dt>{mission.name}</dt>
              {items.map((item) => (
                <dd key={item.source + item.title}>
                  <a href={item.source}>{item.title}</a>, {item.creator}. {item.license}.
                </dd>
              ))}
            </div>
          ))}
        </dl>
      </section>
      <section className="doc-section">
        <h2><Cased>Seals and logos</Cased></h2>
        <p>
          Each agency's seal and logo is reproduced from the source below. Every mission page credits its own. They
          identify the agency each theme is for and imply no endorsement; the <Link to="/disclaimer">Disclaimer</Link>{' '}
          explains more.
        </p>
        <ul className="doc-credits__marks">
          {marks.map((mission) => (
            <li key={mission.id}>
              <Link to={`/missions/${mission.id}`}>{mission.name}</Link>
              {mission.seal && (
                <>
                  : <a href={mission.seal.source}>seal</a>
                </>
              )}
              {mission.logo && (
                <>
                  {mission.seal ? ', ' : ': '}
                  <a href={mission.logo.source}>logo</a>
                </>
              )}
            </li>
          ))}
          <li>
            NASA worm logotype on the home page, Richard Danne and Bruce Blackburn:{' '}
            <a href="https://commons.wikimedia.org/wiki/File:NASA_Worm_logo.svg">Wikimedia Commons</a>, public domain
          </li>
          <li>
            FirstGov.gov logo (2001-2007) on the home page:{' '}
            <a href="https://commons.wikimedia.org/wiki/File:FirstGov.gov_logo_(2001-2007).png">Wikimedia Commons</a>,
            public domain
          </li>
          <li>
            U.S. Digital Service logo on the home page: <a href="https://www.usds.gov/">usds.gov</a>
          </li>
          <li>
            U.S. Web Design System mark on the home page:{' '}
            <a href="https://github.com/uswds/uswds-site">USWDS site repository</a>
          </li>
          <li>
            America by Design wordmark on the home page: <a href="https://americabydesign.gov/">americabydesign.gov</a>
          </li>
        </ul>
      </section>
      <section className="doc-section">
        <h2><Cased>Typefaces</Cased></h2>
        <p>From the Google Fonts catalog under open licenses, self-hosted through Fontsource.</p>
        <ul>
          {typefaces.map((font) => (
            <li key={font.id}>
              <a href={font.specimen}>{font.name}</a>, {font.designer}. {font.license ?? 'SIL Open Font License'}.
            </li>
          ))}
        </ul>
      </section>
    </DocLayout>
  )
}
